import {
  ComplianceMatrixEntry,
  ComplianceMatrixMapping,
  ComplianceMatrixEvidence,
  ComplianceMatrixAssessment,
  ComplianceMatrixSnapshot,
  ComplianceMatrixStatusHistory,
  MatrixDashboardKPIs
} from './types';

const API_BASE = '/api/compliance/matrix';

export const matrixService = {
  async getDashboardKPIs(orgId: string = 'ORG-XYZ'): Promise<MatrixDashboardKPIs> {
    const res = await fetch(`${API_BASE}/analytics/dashboard?organization_id=${orgId}`);
    if (!res.ok) throw new Error('KPI verileri alinamadi');
    return res.json();
  },

  async getMatrixEntries(orgId: string = 'ORG-XYZ', filters?: {
    search?: string;
    compliance_status?: string;
    implementation_status?: string;
    responsible_department?: string;
    reassessment_required?: boolean;
  }): Promise<ComplianceMatrixEntry[]> {
    const params = new URLSearchParams({ organization_id: orgId });
    if (filters?.search) params.append('search', filters.search);
    if (filters?.compliance_status) params.append('compliance_status', filters.compliance_status);
    if (filters?.implementation_status) params.append('implementation_status', filters.implementation_status);
    if (filters?.responsible_department) params.append('responsible_department', filters.responsible_department);
    if (filters?.reassessment_required !== undefined) params.append('reassessment_required', String(filters.reassessment_required));

    const res = await fetch(`${API_BASE}/entries?${params.toString()}`);
    if (!res.ok) throw new Error('Uyum matrisi kayitlari alinamadi');
    return res.json();
  },

  async getMatrixEntryById(id: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixEntry> {
    const res = await fetch(`${API_BASE}/entries/${id}?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Matris kaydi detayi alinamadi');
    return res.json();
  },

  async createMatrixEntry(data: Partial<ComplianceMatrixEntry>, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixEntry> {
    const res = await fetch(`${API_BASE}/entries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Matris kaydi olusturulamadi');
    }
    return res.json();
  },

  async updateMatrixEntry(id: string, updates: Partial<ComplianceMatrixEntry>, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixEntry> {
    const res = await fetch(`${API_BASE}/entries/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Matris kaydi guncellenemedi');
    }
    return res.json();
  },

  async getMappings(matrixEntryId: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixMapping[]> {
    const res = await fetch(`${API_BASE}/entries/${matrixEntryId}/mappings?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Kopru eslestirmeleri alinamadi');
    return res.json();
  },

  async addMapping(matrixEntryId: string, mapping: Partial<ComplianceMatrixMapping>, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixMapping> {
    const res = await fetch(`${API_BASE}/entries/${matrixEntryId}/mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(mapping)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Eslestirme eklenemedi');
    }
    return res.json();
  },

  async deleteMapping(mappingId: string, orgId: string = 'ORG-XYZ'): Promise<void> {
    const res = await fetch(`${API_BASE}/mappings/${mappingId}?organization_id=${orgId}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Eslestirme silinemedi');
  },

  async getEvidences(matrixEntryId: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixEvidence[]> {
    const res = await fetch(`${API_BASE}/entries/${matrixEntryId}/evidences?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Kanit kayitlari alinamadi');
    return res.json();
  },

  async addEvidence(matrixEntryId: string, evidence: Partial<ComplianceMatrixEvidence>, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixEvidence> {
    const res = await fetch(`${API_BASE}/entries/${matrixEntryId}/evidences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(evidence)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Kanit eklenemedi');
    }
    return res.json();
  },

  async checkExpiredEvidences(orgId: string = 'ORG-XYZ'): Promise<{ expired_count: number }> {
    const res = await fetch(`${API_BASE}/evidences/check-expired?organization_id=${orgId}`, {
      method: 'POST'
    });
    if (!res.ok) throw new Error('Kanit gecerlilik taramasi yapilamadi');
    return res.json();
  },

  async getAssessments(matrixEntryId: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixAssessment[]> {
    const res = await fetch(`${API_BASE}/entries/${matrixEntryId}/assessments?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Degerlendirme kayitlari alinamadi');
    return res.json();
  },

  async recordAssessment(matrixEntryId: string, assessment: Partial<ComplianceMatrixAssessment>, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixAssessment> {
    const res = await fetch(`${API_BASE}/entries/${matrixEntryId}/assessments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(assessment)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Degerlendirme kaydedilemedi');
    }
    return res.json();
  },

  async getSnapshots(orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixSnapshot[]> {
    const res = await fetch(`${API_BASE}/snapshots?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Anlik dondurma kayitlari alinamadi');
    return res.json();
  },

  async createSnapshot(snapshot: Partial<ComplianceMatrixSnapshot>, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixSnapshot> {
    const res = await fetch(`${API_BASE}/snapshots`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(snapshot)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Dondurma kaydi olusturulamadi');
    }
    return res.json();
  },

  async getStatusHistory(matrixEntryId: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceMatrixStatusHistory[]> {
    const res = await fetch(`${API_BASE}/entries/${matrixEntryId}/history?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Tarihce kayitlari alinamadi');
    return res.json();
  }
};
